import React from "react";
import { cn } from "@/lib/utils";

/**
 * EmptyState — "there's nothing here yet" primitive.
 *
 * Composition:
 *   • title       — required
 *   • description — optional secondary paragraph
 *   • action      — optional primary CTA slot (usually a <Button/>)
 *   • icon        — optional 24×24 icon slot (consumer-supplied)
 *
 * A11y: `role="status"` so screen readers announce the empty state
 * on route change.
 */

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
  "data-testid": testId,
}) {
  return (
    <div
      role="status"
      data-testid={testId}
      className={cn(
        "flex flex-col items-center justify-center text-center",
        "rounded-[var(--ds-radius-lg)] border border-dashed border-[var(--ds-color-border-strong)]",
        "bg-[var(--ds-color-surface)] px-6 py-10",
        className
      )}
    >
      {icon && (
        <div className="mb-3 text-[var(--ds-color-ink-subtle)]" aria-hidden>
          {icon}
        </div>
      )}
      <h3 className="text-[16px] font-semibold text-[var(--ds-color-ink)]">
        {title}
      </h3>
      {description && (
        <p className="mt-1 max-w-md text-[13px] leading-snug text-[var(--ds-color-ink-muted)]">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export default EmptyState;
